import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFeedback } from "../../../contexts/FeedbackContext.tsx";
import usuarioService from "../../../services/usuarioService.ts";
import Button from "../../../ui/Button.tsx";
import ErrorBanner from "../../../ui/ErrorBanner.tsx";
import PasswordInput from "../../../components/Inputs/PasswordInput/index.tsx";
import { useForm } from "react-hook-form";
import "../../../styles/design-system.css";

const SENHA_MINIMA = 6;

type FormData = { senha: string; confirmarSenha: string };

/**
 * Destino do link enviado por e-mail no fluxo de "esqueci minha senha".
 * Rota publica: quem chega aqui, por definicao, nao esta logado. O que autoriza
 * a troca e o token da query string, que so existe na caixa de e-mail do dono.
 */
const RedefinirSenhaPage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { showFeedback } = useFeedback();

  const token = params.get("token") || "";

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>();
  const [isLoading, setIsLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const onSubmit = async (data: FormData) => {
    if (data.senha !== data.confirmarSenha) {
      setErro("As senhas não coincidem.");
      return;
    }

    try {
      setIsLoading(true);
      setErro(null);
      const resp = await usuarioService.redefinirSenha(token, data.senha);
      showFeedback(resp?.message || "Senha redefinida com sucesso!", "success");
      navigate("/");
    } catch (e: any) {
      setErro(e.message || "Não foi possível redefinir a senha.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="ds ds-login-screen">
      <div className="ds-login-form-wrap">
        <div className="ds-login-form-card">
          <div className="ds-login-header">
            <div className="ds-brand-mark" style={{ width: 44, height: 44, marginBottom: 20 }}>
              <img src="/logo-controlsis.png" alt="ControlSIS" />
            </div>
            <h2>Escolha uma nova senha</h2>
          </div>

          {!token ? (
            <>
              <p className="login-sub">
                Este link está incompleto ou inválido.
              </p>
              <ErrorBanner message="Link inválido. Solicite um novo e-mail de redefinição na tela de login." />
              <div className="ds-login-signup">
                <a href="/" onClick={(e) => { e.preventDefault(); navigate("/"); }}>
                  Voltar para login
                </a>
              </div>
            </>
          ) : (
            <>
              <p className="login-sub">
                Defina a senha que você vai usar para entrar a partir de agora.
              </p>

              <form onSubmit={handleSubmit(onSubmit)} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <PasswordInput
                  label="Nova senha"
                  register={register("senha", {
                    required: "Informe a nova senha",
                    minLength: { value: SENHA_MINIMA, message: `A senha deve ter pelo menos ${SENHA_MINIMA} caracteres` },
                  })}
                  error={errors.senha}
                />
                <PasswordInput
                  label="Confirmar nova senha"
                  register={register("confirmarSenha", { required: "Confirme a nova senha" })}
                  error={errors.confirmarSenha}
                />

                {erro && <ErrorBanner message={erro} />}

                <Button type="submit" variant="primary" disabled={isLoading} style={{ width: "100%" }}>
                  {isLoading ? "Salvando..." : "Salvar nova senha"}
                </Button>

                <div className="ds-login-signup">
                  <a href="/" onClick={(e) => { e.preventDefault(); navigate("/"); }}>
                    Voltar para login
                  </a>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RedefinirSenhaPage;
